import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes, useTheme } from "styled-components";
import Container from "../../../atoms/Container";
import VeriticalCenter from "../../../atoms/VerticalCenter";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import { useResonanceLayout } from "../../ResonanceContext/ResonanceLayoutContext";
import { useIonRouter } from "@ionic/react";
import PWAPrompt from "react-ios-pwa-prompt";
import { MINI_LOGO } from "../../../../constants/images";
import { useAndroidPwaPrompt } from "../../../../hooks/useAndroidPwaPrompt";
import { useStudySlug } from "../../../../hooks/useStudySlug";

type DeviceType = "iOS" | "Android" | "Desktop";

const DisplayRegistredSuccessfully: FC = () => {
  const { t } = useTranslation();
  const study_slug = useStudySlug();
  const theme = useTheme();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  const [deviceType, setDeviceType] = useState<DeviceType>("Desktop");
  const [iosPromptVisible, setIosPromptVisible] = useState(false);

  const { isReady: androidReady, promptInstall, installed } = useAndroidPwaPrompt();

  useEffect(() => {
    setResonanceLayout({
      disableHeader: true,
      disableNav: true,
      backgroundColor: theme["primary"].background,
      welcomePage: false,
    });
    return () =>
      setResonanceLayout({
        disableHeader: false,
        disableNav: false,
        backgroundColor: "white",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor;
    if (/android/i.test(ua)) setDeviceType("Android");
    else if (/iPad|iPhone|iPod/.test(ua)) setDeviceType("iOS");
    else setDeviceType("Desktop");
  }, []);

  // For iOS, we only show the modal (no card)
  useEffect(() => {
    if (deviceType === "iOS") {
      // small delay so the page content settles before the modal
      const id = setTimeout(() => setIosPromptVisible(true), 500);
      return () => clearTimeout(id);
    } else {
      setIosPromptVisible(false);
    }
  }, [deviceType]);

  const handleNext = () => {
    ionRouter.push(`/${study_slug}/tabs/home`, "root", "replace", { unmount: true });
  };

  const showAndroidCard = deviceType === "Android" && !installed;

  return (
    <Background>
      <Container>
        <VeriticalCenter style={{ alignItems: "center", marginTop: "6vh" }}>
          <Icon alt="registered successfully" />
          <Title>{t("home_registred_successfully")}</Title>

          {/* ANDROID: keep the beautiful card & CTA */}
          {showAndroidCard && (
            <HeroCard role="status" aria-live="polite">
              <Logo src={MINI_LOGO} alt="MentalPlus" />
              <Headline>
                {t("add_to_home_screen_prompt") || "Ajoutez MentalPlus sur votre écran d’accueil."}
              </Headline>

              <InstallRow>
                {androidReady ? (
                  <PrimaryButton onClick={promptInstall} aria-label="Installer l'application MentalPlus">
                    Installer l'application
                  </PrimaryButton>
                ) : (
                  <Hint>
                    Ouvrez le menu <kbd>⋮</kbd> puis choisissez <strong>Installer l'application</strong>.
                  </Hint>
                )}
              </InstallRow>
            </HeroCard>
          )}

          {/* iOS: ONLY the PWAPrompt modal — no card */}
          {deviceType === "iOS" && (
            <PWAPrompt
              isShown={iosPromptVisible}
              onClose={() => setIosPromptVisible(false)}
              appIconPath="/assets/images/logo.png"
              copyTitle="Installer MentalPlus"
              copySubtitle="Ajoutez cette application à votre écran d’accueil"
              copyDescription="Touchez le bouton Partager, puis sélectionnez « Sur l’écran d’accueil »."
              copyShareStep="1. Appuyez sur le bouton Partager."
              copyAddToHomeScreenStep="2. Sélectionnez « Sur l’écran d’accueil »."
              delay={0}           // we control visibility via isShown
              timesToShow={1}     // show once
              promptOnVisit={1}   // and only on this visit
            />
          )}

          <NextPrevButtons onNextClick={handleNext} />
        </VeriticalCenter>
      </Container>
    </Background>
  );
};

/* ---------- Styles ---------- */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Background = styled.div`
  min-height: 100vh;
`;

const HeroCard = styled.section`
  width: min(720px, 90vw);
  margin: 20px auto 28px;
  padding: clamp(18px, 3vw, 28px);
  border-radius: 18px;
  animation: ${fadeUp} 0.36s ease-out both;

  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: saturate(140%) blur(12px);
  border: 1px solid rgba(120, 140, 180, 0.2);
  box-shadow:
    0 10px 30px rgba(20, 45, 90, 0.1),
    0 2px 8px rgba(20, 45, 90, 0.06);
  text-align: center;
`;

const Icon = styled.img`
  width: 96px;
  content: url(/assets/images/svg/verified-green.svg);
  margin: 0 0 6px;
`;

const Title = styled.h1`
  font-size: clamp(20px, 2.2vw, 28px);
  margin: 0 0 50px;
  font-weight: 800;
  color: ${(p) => p.theme.primary?.contrast ?? "#0d1b2a"};
  text-align: center;
`;

const Logo = styled.img`
  width: 88px;
  height: 88px;
  border-radius: 20px;
  margin: 6px auto 14px;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
`;

const Headline = styled.p`
  font-size: clamp(16px, 1.8vw, 20px);
  margin: 0 auto 12px;
  line-height: 1.4;
  color: #1f2a44;
`;

const InstallRow = styled.div`
  display: grid;
  gap: 14px;
  align-items: center;
  justify-items: center;
  margin-top: 10px;
`;

const kbdStyle = `
  font-family: inherit;
  font-weight: 700;
  padding: 0 8px;
  border-radius: 6px;
  background: rgba(0,0,0,.06);
  border: 1px solid rgba(0,0,0,.08);
`;

const Hint = styled.p`
  margin: 0;
  color: #34435e;
  font-size: 15px;
  kbd { ${kbdStyle} }
  strong { font-weight: 800; }
`;

const PrimaryButton = styled.button`
  appearance: none;
  border: 0;
  padding: 12px 18px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.2px;
  cursor: pointer;

  background: linear-gradient(180deg, ${(p) => p.theme.primary?.main ?? "#2563eb"} 0%, ${(p) =>
    p.theme.primary?.main ?? "#2563eb"} 85%) no-repeat;
  color: #fff;
  box-shadow:
    0 10px 20px rgba(37, 99, 235, 0.24),
    0 2px 6px rgba(37, 99, 235, 0.2);
  transition: transform 0.08s ease, box-shadow 0.12s ease, filter 0.12s ease;

  &:hover { transform: translateY(-1px); filter: brightness(1.02); }
  &:active { transform: translateY(0); box-shadow:
    0 6px 14px rgba(37, 99, 235, 0.22),
    0 1px 4px rgba(37, 99, 235, 0.2); }
`;

export default DisplayRegistredSuccessfully;
